import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { jobsAPI } from '../../services/api';
import { formatSalary, timeAgo, truncate, JOB_TYPES, EXPERIENCE_LEVELS } from '../../utils/helpers';
import { FiSearch, FiMapPin, FiFilter, FiX, FiClock, FiBriefcase } from 'react-icons/fi';
import './Jobs.css';

const JobsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    q: searchParams.get('q') || '',
    location: searchParams.get('location') || '',
    job_type: searchParams.get('job_type') || '',
    experience_level: searchParams.get('experience_level') || '',
    salary_min: searchParams.get('salary_min') || '',
    salary_max: searchParams.get('salary_max') || '',
    category: searchParams.get('category') || '',
  });

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });

      const endpoint = filters.q ? jobsAPI.search : jobsAPI.list;
      const { data } = await endpoint(params);
      const d = data.data || data.jobs || data.results || (Array.isArray(data) ? data : []);
      setJobs(d);
      setTotal(data.total || data.count || d.length);
    } catch (err) {
      setJobs(placeholderJobs);
      setTotal(placeholderJobs.length);
    }
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    setSearchParams(params);
    fetchJobs();
  };

  const clearFilters = () => {
    setFilters({ q: '', location: '', job_type: '', experience_level: '', salary_min: '', salary_max: '', category: '' });
    setSearchParams({});
    setPage(1);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="jobs-page">
      <div className="container">
        <div className="page-header">
          <h1>Browse Jobs</h1>
          <p>Find your next opportunity from {total}+ listings across South Africa</p>
        </div>

        {/* Search */}
        <form className="jobs-search-bar" onSubmit={handleSearch}>
          <div className="jobs-search-field">
            <FiSearch />
            <input type="text" placeholder="Job title, skill, or keyword..." value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          </div>
          <div className="jobs-search-field">
            <FiMapPin />
            <input type="text" placeholder="Location..." value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })} />
          </div>
          <button type="submit" className="btn btn-primary">Search</button>
          <button type="button" className="btn btn-secondary filter-toggle" onClick={() => setShowFilters(!showFilters)}>
            <FiFilter /> Filters {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount}</span>}
          </button>
        </form>

        {/* Filters Panel */}
        {showFilters && (
          <div className="filters-panel card animate-fade-in">
            <div className="filters-grid">
              <div className="form-group">
                <label>Job Type</label>
                <select value={filters.job_type} onChange={(e) => setFilters({ ...filters, job_type: e.target.value })}>
                  <option value="">All Types</option>
                  {JOB_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Experience</label>
                <select value={filters.experience_level} onChange={(e) => setFilters({ ...filters, experience_level: e.target.value })}>
                  <option value="">All Levels</option>
                  {EXPERIENCE_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Min Salary (ZAR)</label>
                <input type="number" placeholder="e.g. 300000" value={filters.salary_min}
                  onChange={(e) => setFilters({ ...filters, salary_min: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Max Salary (ZAR)</label>
                <input type="number" placeholder="e.g. 800000" value={filters.salary_max}
                  onChange={(e) => setFilters({ ...filters, salary_max: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={handleSearch}>Apply Filters</button>
              <button className="btn btn-secondary btn-sm" onClick={clearFilters}><FiX /> Clear All</button>
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : jobs.length > 0 ? (
          <>
            <div className="results-meta">{total} jobs found</div>
            <div className="jobs-list-grid">
              {jobs.map((job, i) => (
                <Link to={`/jobs/${job.id}`} className="job-list-card" key={job.id || i} style={{ animationDelay: `${i * 0.05}s` }}>
                  <div className="job-list-left">
                    <div className="job-company-logo">{(job.company_name || 'C')[0]}</div>
                    <div>
                      <h3>{job.title}</h3>
                      <div className="job-list-meta">
                        <span>{job.company_name || 'Company'}</span>
                        <span><FiMapPin /> {job.location_name || job.location || 'South Africa'}</span>
                        <span><FiClock /> {timeAgo(job.created_at) || 'Recently'}</span>
                      </div>
                      <p className="job-list-desc">{truncate(job.description || job.summary || '', 200)}</p>
                      <div className="job-list-tags">
                        <span className="badge">{job.job_type || 'Full-time'}</span>
                        {job.experience_level && <span className="badge badge-purple">{job.experience_level}</span>}
                        {job.is_remote && <span className="badge badge-success">Remote</span>}
                      </div>
                    </div>
                  </div>
                  <div className="job-list-right">
                    <div className="job-salary">{formatSalary(job.salary_min, job.salary_max)}</div>
                  </div>
                </Link>
              ))}
            </div>

            {total > 12 && (
              <div className="pagination">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Page {page} of {Math.ceil(total / 12)}</span>
                <button disabled={page >= Math.ceil(total / 12)} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <div className="icon">🔍</div>
            <h3>No jobs found</h3>
            <p>Try adjusting your search or filters</p>
            <button className="btn btn-primary" onClick={clearFilters}>Clear Filters</button>
          </div>
        )}
      </div>
    </div>
  );
};

const placeholderJobs = [
  { id: 1, title: 'Senior Software Developer', company_name: 'TechCorp SA', location: 'Johannesburg', job_type: 'Full-time', salary_min: 650000, salary_max: 900000, experience_level: 'Senior', description: 'Build cutting-edge fintech solutions for the African market using Python, React, and cloud technologies.' },
  { id: 2, title: 'Data Analyst', company_name: 'Analytics Co', location: 'Cape Town', job_type: 'Full-time', salary_min: 400000, salary_max: 550000, experience_level: 'Mid', description: 'Analyze complex datasets and deliver actionable insights using SQL, Python, and visualization tools.' },
  { id: 3, title: 'Project Manager', company_name: 'BuildRight', location: 'Pretoria', job_type: 'Contract', salary_min: 500000, salary_max: 750000, experience_level: 'Senior', description: 'Lead enterprise projects with agile methodology and cross-functional team management.' },
  { id: 4, title: 'UX Designer', company_name: 'DesignHub', location: 'Durban', job_type: 'Full-time', salary_min: 350000, salary_max: 500000, experience_level: 'Mid', description: 'Design user interfaces for web and mobile platforms with a focus on accessibility and conversion.' },
  { id: 5, title: 'Financial Accountant', company_name: 'FinServe', location: 'Sandton', job_type: 'Full-time', salary_min: 450000, salary_max: 600000, experience_level: 'Mid', description: 'Manage financial reporting, IFRS compliance, and internal controls for a growing firm.' },
  { id: 6, title: 'DevOps Engineer', company_name: 'CloudFirst', location: 'Remote', job_type: 'Full-time', salary_min: 600000, salary_max: 850000, experience_level: 'Senior', description: 'Build CI/CD pipelines and manage cloud infrastructure on AWS and Azure with Kubernetes.' },
];

export default JobsList;
